import { createClient } from '@libsql/client';
import { nanoid } from 'nanoid';

const db = createClient({
  url: import.meta.env.VITE_TURSO_DB_URL,
  authToken: import.meta.env.VITE_TURSO_AUTH_TOKEN
});

export const inquiryService = {
  async createContactInquiry(inquiry) {
    const id = nanoid();

    await db.execute({
      sql: `INSERT INTO inquiries (
        id, property_id, type, name, email, phone, message, status
      ) VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
      args: [
        id,
        inquiry.property_id || null,
        'contact',
        inquiry.name,
        inquiry.email,
        inquiry.phone || null,
        inquiry.message,
        'pending'
      ],
    });

    return this.getContactInquiryById(id);
  },

  async createValuationRequest(request) {
    const id = nanoid();

    await db.execute({
      sql: `INSERT INTO valuation_request (
        id, property_type, location, area, name, email, phone, additional_info
      ) VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
      args: [
        id,
        request.property_type,
        request.location,
        request.area,
        request.name,
        request.email,
        request.phone || null,
        request.additional_info || null,
      ],
    });

    return this.getValuationRequestById(id);
  },

  async createPropertyInquiry(inquiry) {
    try {
      const id = nanoid();
      const createdAt = new Date().toISOString();

      await db.execute({
        sql: `INSERT INTO propertyinquiries
              (id, property_url, property_id, name, email, phone, message, status, created_at)
              VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        args: [
          id,
          inquiry.property_url,
          inquiry.property_id,
          inquiry.name,
          inquiry.email,
          inquiry.phone || null,
          inquiry.message,
          inquiry.status || 'new',
          createdAt
        ],
      });

      return { id, ...inquiry, created_at: createdAt };
    } catch (error) {
      console.error('Error creating property inquiry:', error);
      throw error;
    }
  },
  
  //  Loan Request
  async createLoanRequest(request) {
    try {
      const id = nanoid();
      const createdAt = new Date().toISOString();
      await db.execute({
        sql: `INSERT INTO loan_requests
              (id, name, email, phone, loan_type, status, created_at)
              VALUES (?, ?, ?, ?, ?, ?, ?)`,
        args: [
          id,
          request.name,
          request.email,
          request.phone || null,
          request.loan_type || 'unspecified',
          request.status || 'new',
          createdAt
        ],
      });
      return { id, ...request, created_at: createdAt };
    } catch (error) {
      console.error('Error creating loan request:', error);
      throw error;
    }
  },

  async getContactInquiryById(id) {
    const result = await db.execute({
      sql: 'SELECT * FROM inquiries WHERE id = ?',
      args: [id],
    });

    if (!result.rows[0]) {
      throw new Error('Contact inquiry not found');
    }

    return result.rows[0];
  },

  async getValuationRequestById(id) {
    const result = await db.execute({
      sql: 'SELECT * FROM valuation_request WHERE id = ?',
      args: [id],
    });

    if (!result.rows[0]) {
      throw new Error('Valuation request not found');
    }

    const request = result.rows[0];
    return {
      ...request,
      area: Number(request.area),
    };
  },

  async getPropertyInquiryById(id) {
    try {
      const result = await db.execute({
        sql: `SELECT * FROM propertyinquiries WHERE id = ?`,
        args: [id],
      });

      if (!result.rows[0]) {
        throw new Error('Property inquiry not found');
      }

      return result.rows[0];
    } catch (error) {
      console.error('Error fetching property inquiry by ID:', error);
      throw error;
    }
  },
  
  //  Loan Request
  async getLoanRequestById(id) {
    try {
      const result = await db.execute({
        sql: `SELECT * FROM loan_requests WHERE id = ?`,
        args: [id],
      });
      if (!result.rows[0]) {
        throw new Error('Loan request not found');
      }
      return result.rows[0];
    } catch (error) {
      console.error('Error fetching loan request by ID:', error);
      throw error;
    }
  },

  async getAllContactInquiries({ status } = {}) {
    const query = status
      ? 'SELECT * FROM inquiries WHERE type = ? AND status = ? ORDER BY created_at DESC'
      : 'SELECT * FROM inquiries WHERE type = ? ORDER BY created_at DESC';
    const args = status ? ['contact', status] : ['contact'];

    const result = await db.execute({ sql: query, args });

    return {
      inquiries: result.rows,
      total: result.rows.length,
    };
  },

  async getAllValuationRequests({ status } = {}) {
    try {
      const query = status
        ? 'SELECT * FROM valuation_request WHERE status = ? ORDER BY created_at DESC'
        : 'SELECT * FROM valuation_request ORDER BY created_at DESC';
      const args = status ? [status] : [];

      const result = await db.execute({ sql: query, args });
      return {
        inquiries: result.rows,
        total: result.rows.length
      };
    } catch (error) {
      console.error('Error fetching valuation requests:', error);
      return { inquiries: [], total: 0 };
    }
  },

  async getAllPropertyInquiries({ status } = {}) {
    try {
      const query = status
        ? 'SELECT * FROM propertyinquiries WHERE status = ? ORDER BY created_at DESC'
        : 'SELECT * FROM propertyinquiries ORDER BY created_at DESC';

      const args = status ? [status] : [];

      const result = await db.execute({ sql: query, args });

      return {
        inquiries: result.rows,
        total: result.rows.length
      };
    } catch (error) {
      console.error('Error fetching property inquiries:', error);
      return { inquiries: [], total: 0 };
    }
  },

  //  Loan Request
  async getAllLoanRequests({ status } = {}) {
    try {
      const query = status
        ? 'SELECT * FROM loan_requests WHERE status = ? ORDER BY created_at DESC'
        : 'SELECT * FROM loan_requests ORDER BY created_at DESC';
      const args = status ? [status] : [];

      const result = await db.execute({ sql: query, args });

      return {
        inquiries: result.rows,
        total: result.rows.length
      };
    } catch (error) {
      console.error('Error fetching loan requests:', error);
      return { inquiries: [], total: 0 };
    }
  },


  async updateContactInquiryStatus(id, status) {
    await db.execute({
      sql: 'UPDATE inquiries SET status = ? WHERE id = ?',
      args: [status, id],
    });

    return this.getContactInquiryById(id);
  },

  async updateValuationRequestStatus(id, status) {
    await db.execute({
      sql: 'UPDATE valuation_request SET status = ? WHERE id = ?',
      args: [status, id],
    });

    return this.getValuationRequestById(id);
  },

  async updatePropertyInquiryStatus(id, status) {
    try {
      await db.execute({
        sql: 'UPDATE propertyinquiries SET status = ? WHERE id = ?',
        args: [status, id]
      });

      return true;
    } catch (error) {
      console.error('Error updating property inquiry status:', error);
      throw error;
    }
  },

  //  Loan Request
  async updateLoanRequestStatus(id, status) {
    try {
      await db.execute({
        sql: 'UPDATE loan_requests SET status = ? WHERE id = ?',
        args: [status, id]
      });
      return true;
    } catch (error) {
      console.error('Error updating loan request status:', error);
      throw error;
    }
  },


  async getFilteredContactInquiries(filter = {}, options = {}) {
    const { limit = 10, offset = 0 } = options;
    const conditions = ["type = 'contact'"];
    const args = [];

    if (filter.status) {
      conditions.push('status = ?');
      args.push(filter.status);
    }

    if (filter.dateFrom) {
      conditions.push('createdAt >= ?');
      args.push(filter.dateFrom);
    }

    if (filter.dateTo) {
      conditions.push('createdAt <= ?');
      args.push(filter.dateTo);
    }

    if (filter.searchTerm) {
      conditions.push('(name LIKE ? OR email LIKE ? OR message LIKE ?)');
      const searchPattern = `%${filter.searchTerm}%`;
      args.push(searchPattern, searchPattern, searchPattern);
    }

    const whereClause = `WHERE ${conditions.join(' AND ')}`;
    const sortClause = filter.sortBy
      ? `ORDER BY ${filter.sortBy} ${filter.sortOrder || 'desc'}`
      : 'ORDER BY createdAt DESC';

    const [inquiries, countResult] = await Promise.all([
      db.execute({
        sql: `SELECT * FROM inquiries ${whereClause} ${sortClause} LIMIT ? OFFSET ?`,
        args: [...args, limit, offset]
      }),
      db.execute({
        sql: `SELECT COUNT(*) as total FROM inquiries ${whereClause}`,
        args
      })
    ]);

    return {
      inquiries: inquiries.rows,
      total: Number(countResult.rows[0].total)
    };
  },


  async getContactInquiryStats() {
    const result = await db.execute({
      sql: `SELECT
              COUNT(*) as total_count,
              COUNT(CASE WHEN status = 'pending' OR status = 'new' THEN 1 END) as new_count,
              COUNT(CASE WHEN status = 'in-progress' THEN 1 END) as in_progress_count,
              COUNT(CASE WHEN status = 'completed' THEN 1 END) as completed_count
            FROM inquiries WHERE type = 'contact'`,
    });
    return result.rows[0];
  },

  async getAllInquiries() {
    try {
      const [
        contactInquiriesResult,
        valuationRequestsResult,
        propertyInquiriesResult,
        loanRequestsResult
      ] = await Promise.all([
        this.getAllContactInquiries(),
        this.getAllValuationRequests(),
        this.getAllPropertyInquiries(),
        this.getAllLoanRequests(),
      ]);

      const allInquiries = [
        ...contactInquiriesResult.inquiries.map(inq => ({ ...inq, type: 'contact', createdAt: inq.created_at || inq.createdAt })),
        ...valuationRequestsResult.inquiries.map(inq => ({ ...inq, type: 'valuation', createdAt: inq.created_at || inq.createdAt })),
        ...propertyInquiriesResult.inquiries.map(inq => ({ ...inq, type: 'property', createdAt: inq.created_at || inq.createdAt })),
        ...loanRequestsResult.inquiries.map(inq => ({ ...inq, type: 'loan', createdAt: inq.created_at || inq.createdAt })),
      ];

      return allInquiries;
    } catch (error) {
      console.error('Error fetching all inquiries:', error);
      return [];
    }
  },

  async getOverallInquiryStats() {
    try {
      const allInquiries = await this.getAllInquiries();
      const total = allInquiries.length;
      const newInquiries = allInquiries.filter(inq => inq.status === 'new' || inq.status === 'pending');
      const newCount = newInquiries.length;

      return {
        totalInquiries: total,
        newInquiriesCount: newCount,
      };
    } catch (error) {
      console.error('Error getting overall inquiry stats:', error);
      return { totalInquiries: 0, newInquiriesCount: 0 };
    }
  },

  //  Delete Property Inquiry
  async deletePropertyInquiry(id) {
    try {
      await db.execute({
        sql: 'DELETE FROM property_inquiries WHERE id = ?',
        args: [id]
      });

      return true;
    } catch (error) {
      console.error('Error deleting property inquiry:', error);
      throw error;
    }
  },

  async delete(id) {
    try {
      await db.execute({
        sql: 'DELETE FROM inquiries WHERE id = ?',
        args: [id]
      });

      return true;
    } catch (error) {
      console.error('Error deleting inquiry:', error);
      throw error;
    }
  },

  async getNewInquiriesLast24Hours() {
    try {
      const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

      const [
        contactInquiriesResult,
        valuationRequestsResult,
        propertyInquiriesResult,
        loanRequestsResult
      ] = await Promise.all([
        db.execute({
          sql: `SELECT * FROM inquiries
                WHERE (status = 'new' OR status = 'pending')
                AND created_at >= ? ORDER BY created_at DESC`,
          args: [twentyFourHoursAgo],
        }),
        db.execute({
          sql: `SELECT * FROM valuation_request
                WHERE (status = 'new' OR status = 'pending')
                AND created_at >= ? ORDER BY created_at DESC`,
          args: [twentyFourHoursAgo],
        }),
        db.execute({
          sql: `SELECT * FROM propertyinquiries
                WHERE (status = 'new' OR status = 'pending')
                AND created_at >= ? ORDER BY created_at DESC`,
          args: [twentyFourHoursAgo],
        }),
        db.execute({
          sql: `SELECT * FROM loan_requests
                WHERE (status = 'new' OR status = 'pending')
                AND created_at >= ? ORDER BY created_at DESC`,
          args: [twentyFourHoursAgo],
        }),
      ]);

      const allNewInquiries = [
        ...contactInquiriesResult.rows.map(inq => ({ ...inq, type: 'contact' })),
        ...valuationRequestsResult.rows.map(inq => ({ ...inq, type: 'valuation' })),
        ...propertyInquiriesResult.rows.map(inq => ({ ...inq, type: 'property' })),
        ...loanRequestsResult.rows.map(inq => ({ ...inq, type: 'loan' })),
      ];

      return allNewInquiries.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    } catch (error) {
      console.error('Error fetching new inquiries from last 24 hours:', error);
      throw error;
    }
  },
};